import { DataTableColumnHeader } from '@/components/tables/DataTableColumnHeader'
import { Typography } from '@/components/ui/typography'
import { formatNumber } from '@/lib/utils'
import type { ClosedDaysReportLocation } from '@/lib/services/reports/reports.types'
import type { ColumnDef } from '@tanstack/react-table'

type ClosedDaysValueKey = keyof Pick<
  ClosedDaysReportLocation,
  'pickup_closed_days' | 'delivery_closed_days' | 'overlap_closed_days'
>

function ClosedDaysValue({ value }: { value: ClosedDaysReportLocation[ClosedDaysValueKey] }) {
  return value === null ? (
    <Typography variant="supporting">Not offered</Typography>
  ) : (
    <span>{formatNumber(value)}</span>
  )
}

export function getClosedDaysColumns(): ColumnDef<ClosedDaysReportLocation>[] {
  return [
    {
      id: 'location_name',
      accessorKey: 'location_name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Location" />,
      enableSorting: false,
      cell: ({ row }) => (
        <Typography variant="labelStrong">{row.original.location_name}</Typography>
      ),
      meta: {
        label: 'Location',
        kind: 'primary',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'px-4 py-3',
      },
    },
    ...(
      [
        ['pickup_closed_days', 'Pickup'],
        ['delivery_closed_days', 'Delivery'],
        ['overlap_closed_days', 'Both'],
      ] satisfies [ClosedDaysValueKey, string][]
    ).map(
      ([key, label]): ColumnDef<ClosedDaysReportLocation> => ({
        id: key,
        accessorKey: key,
        header: ({ column }) => <DataTableColumnHeader column={column} title={label} />,
        enableSorting: false,
        cell: ({ row }) => <ClosedDaysValue value={row.original[key]} />,
        meta: {
          label,
          kind: 'number',
          align: 'end',
          headClassName: 'whitespace-nowrap px-4',
          cellClassName: 'whitespace-nowrap px-4 py-3 font-medium tabular-nums',
        },
      })
    ),
  ]
}
